import React from 'react'
import { Field } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import { FormFieldError, FormFieldLabel, FormTextArea } from '../atoms/FormTextInput'
import InsetSegmentedControl from '../atoms/InsetSegmentedControl'
import {
  applySellContractPatch,
  sellContractFieldClass,
  SellContractFormControls,
  ShowSellContractError
} from './SellContractSegmentField'
import { SellContractFormData } from 'src/helpers/sellContractFormValidation'

interface SellContractAgreementSectionProps {
  values: SellContractFormData
  form: SellContractFormControls
  showError: ShowSellContractError
}

type YesNo = 'yes' | 'no'

const SellContractAgreementSection = ({ values, form, showError }: SellContractAgreementSectionProps): JSX.Element => {
  const { t } = useTranslation()
  const business = values.client_type === 'company'

  return (
    <div className="self-stretch flex flex-col justify-start items-start gap-6">
      {business ? null : (
        <>
          <Field name="has_objective_deviations">
            {({ input }) => (
              <div className={sellContractFieldClass}>
                <FormFieldLabel label={t('sell_contract.objective_deviations')} />
                <InsetSegmentedControl
                  className="w-28"
                  value={input.value === true ? 'yes' : 'no'}
                  onChange={(nextValue: YesNo) => {
                    const enabled = nextValue === 'yes'
                    input.onChange(enabled)
                    applySellContractPatch(form, values, { has_objective_deviations: enabled })
                  }}
                  options={[
                    { value: 'yes', label: t('sell_contract.yes') },
                    { value: 'no', label: t('sell_contract.no') }
                  ]}
                />
              </div>
            )}
          </Field>
          {values.has_objective_deviations ? (
            <Field name="objective_deviation_1">
              {({ input, meta }) => (
                <div className={sellContractFieldClass}>
                  <FormFieldLabel label={t('sell_contract.objective_deviation_1')} />
                  <FormTextArea
                    value={input.value ?? ''}
                    onChange={input.onChange}
                    placeholder={t('sell_contract.objective_deviation_placeholder')}
                    error={showError(meta)}
                  />
                  <FormFieldError show={showError(meta)} message={t('sell_contract.field_required')} />
                </div>
              )}
            </Field>
          ) : null}
        </>
      )}
      <Field name="contract_note">
        {({ input }) => (
          <div className={sellContractFieldClass}>
            <FormFieldLabel label={t('sell_contract.further_agreements')} />
            <FormTextArea
              value={input.value ?? ''}
              onChange={input.onChange}
              placeholder={t('sell_contract.further_agreements_placeholder')}
            />
          </div>
        )}
      </Field>
      {business ? null : (
        <>
          <Field name="exclude_update_obligations">
            {({ input }) => (
              <div className={sellContractFieldClass}>
                <FormFieldLabel label={t('sell_contract.exclude_updates')} />
                <InsetSegmentedControl
                  className="w-28"
                  value={input.value === false ? 'no' : 'yes'}
                  onChange={(nextValue: YesNo) => {
                    input.onChange(nextValue === 'yes')
                  }}
                  options={[
                    { value: 'yes', label: t('sell_contract.yes') },
                    { value: 'no', label: t('sell_contract.no') }
                  ]}
                />
              </div>
            )}
          </Field>
          <Field name="shorten_warranty">
            {({ input }) => (
              <div className={sellContractFieldClass}>
                <FormFieldLabel label={t('sell_contract.shorten_warranty')} />
                <InsetSegmentedControl
                  className="w-28"
                  value={input.value === false ? 'no' : 'yes'}
                  onChange={(nextValue: YesNo) => {
                    input.onChange(nextValue === 'yes')
                  }}
                  options={[
                    { value: 'yes', label: t('sell_contract.yes') },
                    { value: 'no', label: t('sell_contract.no') }
                  ]}
                />
              </div>
            )}
          </Field>
        </>
      )}
    </div>
  )
}

export default SellContractAgreementSection
